import type { NotificationObjectType } from "@/types/NotificationTypes";
import type { SxProps } from "@mui/material";

import React from "react";
import { isAxiosError } from "axios";
import { useSWRConfig } from "swr";
import { Box, Button, Theme, Typography, alpha } from "@mui/material";
import TimezoneUtils from "@/utils/TimezoneUtils";
import { timeAgo } from "@/utils/timeUtils";
import { dismissNotification, restoreNotification } from "@/api/notification";
import {
  captureException,
  captureMessage,
  withScope,
} from "@/observability/sentry";
import { getFetcherKey } from "@/hooks/useSWR";

type BasePropType = { children: React.ReactNode };
export type VariantPropType = {
  variant: "default" | "primary" | "info" | "success" | "warning" | "error";
  sx?: SxProps<Theme>;
};

type NotificationComponentType = React.FC<BasePropType & VariantPropType> & {
  Title: React.FC<
    BasePropType & { created_at: NotificationObjectType["created_at"] }
  >;
  Content: React.FC<BasePropType>;
  Actions: React.FC<{
    id: number;
    is_dismissable: boolean;
    dismissed_at: string | null;
    children?: React.ReactNode;
  }>;
};

const Title: NotificationComponentType["Title"] = ({
  created_at,
  children,
}) => {
  return (
    <Box component="header" data-testid="notification-item">
      <Typography variant="caption" color="text.secondary">
        {timeAgo(TimezoneUtils.zonedDate(created_at).toISOString())}
      </Typography>
      <Typography
        component="div"
        variant="body1"
        fontWeight="bold"
        color="text.primary"
        sx={{ mb: 2, cursor: "default" }}
        data-testid="notification-title"
      >
        {children}
      </Typography>
    </Box>
  );
};

const Content: NotificationComponentType["Content"] = ({ children }) => (
  <Typography
    component="div"
    color="text.secondary"
    variant="body2"
    sx={{ cursor: "default" }}
  >
    {children}
  </Typography>
);

const Actions: NotificationComponentType["Actions"] = ({
  id,
  children,
  dismissed_at,
  is_dismissable,
}) => {
  const { mutate } = useSWRConfig();
  const [loading, setLoading] = React.useState(false);
  const dismiss = React.useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      e.preventDefault();
      setLoading(true);

      try {
        await dismissNotification(id);
        await mutate(getFetcherKey("/notification/?dismissed_at__isnull=True"));
      } catch (error) {
        withScope((scope) => {
          scope.setLevel("error");
          captureException(error);
          captureMessage("There was an issue dismissing notification");
        });
      } finally {
        setLoading(false);
      }
    },
    [id, mutate]
  );

  const restore = React.useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      e.preventDefault();
      setLoading(true);
      try {
        await restoreNotification(id);
        await mutate(
          getFetcherKey("/notification/?dismissed_at__isnull=False")
        );
        await mutate(getFetcherKey("/notification/?dismissed_at__isnull=True"));
      } catch (error) {
        // Offline/aborted requests are client-side conditions, not app
        // failures — the catch already degrades gracefully. Keep them out
        // of Sentry. PROXYCONTROLPANELDASHBOARD-7EM.
        const isClientNetworkError =
          isAxiosError(error) &&
          ["ECONNABORTED", "ERR_NETWORK"].includes(error.code || "");
        if (!isClientNetworkError) {
          captureException(
            new Error("There was an issue restoring notification", {
              cause: error,
            })
          );
        }
      } finally {
        setLoading(false);
      }
    },
    [id, mutate]
  );

  return (
    <Box
      sx={{ mt: 3, mb: 2 }}
      display="flex"
      justifyContent="flex-start"
      alignItems="center"
    >
      {children && children}
      {/* TODO: Coloring
        Use theme neutal here instead of sx props after asking design team.
      */}
      {is_dismissable && (
        <Button
          disabled={loading}
          loading={loading}
          data-testid="notification-dismiss-button"
          size="small"
          variant="text"
          disableElevation
          onClick={dismissed_at ? restore : dismiss}
          sx={[
            {
              px: 3,
              color: ({ palette }) => palette.text.secondary,
            },
            children
              ? {
                  ml: 2,
                }
              : {
                  ml: 0,
                },
          ]}
        >
          {dismissed_at ? "Unarchive" : "Archive"}
        </Button>
      )}
    </Box>
  );
};

const NotificationItem: NotificationComponentType = ({
  children,
  variant,
  sx,
}) => {
  return (
    <Box
      data-testid={`notification-${variant}`}
      sx={{
        width: "100%",
        p: 3,
        backgroundColor: (theme) =>
          variant === "default"
            ? theme.palette.background.default
            : alpha(theme.palette[variant].main, 0.08),
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

NotificationItem.Title = Title;
NotificationItem.Content = Content;
NotificationItem.Actions = Actions;

export type NotificationItemComponent = React.FC<{
  children?: React.ReactNode;
  item: NotificationObjectType;
}>;
export default NotificationItem;
